import { TagState } from '@custom-components/general/tag/tag.component';

// The three ratios the performance card judges. `null` means "not evaluable": either the
// window holds too few valid days, or no expected yield is known for it. A `0` is a real
// measurement (a site that produced nothing) and must not be conflated with `null`.
export interface PerformanceRatios {
  lastCompleteDay: number | null;
  sevenDay: number | null;
  thirtyDay: number | null;
}

export interface PerformanceVerdict {
  state: TagState;
  labelKey: string;
}

export const PRODUCTION_STOPPED_BELOW = 0.35;
export const UNDERPERFORMING_BELOW = 0.7;
export const CHECK_CONFIGURATION_ABOVE = 1.1;

const VERDICT_KEY = 'devices.detailTabs.insights.performance.verdict';

/**
 * Conditions are evaluated in the order the product spec numbers them, and the first match
 * wins.
 *
 * Condition 4 of that list is deliberately absent below. It is not specified yet, and
 * leaving a visible gap keeps every other condition on its original number, so a later
 * answer slots in without renumbering — and without silently shifting which condition
 * takes precedence over which.
 *
 * The verdict describes the current measurement only. It is deliberately not derived from
 * alarm state: a site can read a perfectly healthy ratio while an earlier alarm is still
 * open, so a chip driven by alarms would contradict the numbers next to it.
 */
export function performanceVerdict(ratios: PerformanceRatios): PerformanceVerdict {
  const { lastCompleteDay, sevenDay, thirtyDay } = ratios;

  if (lastCompleteDay !== null && lastCompleteDay < PRODUCTION_STOPPED_BELOW) {
    return { state: 'error', labelKey: `${VERDICT_KEY}.productionStopped` };
  }

  if (sevenDay !== null && sevenDay < UNDERPERFORMING_BELOW) {
    return { state: 'warning', labelKey: `${VERDICT_KEY}.underperforming` };
  }

  if (thirtyDay !== null && thirtyDay > CHECK_CONFIGURATION_ABOVE) {
    return { state: 'warning', labelKey: `${VERDICT_KEY}.checkConfiguration` };
  }

  // Condition 4 — awaiting specification, see the note above.

  if (sevenDay !== null && sevenDay >= UNDERPERFORMING_BELOW) {
    return { state: 'success', labelKey: `${VERDICT_KEY}.withinNormalRange` };
  }

  return { state: 'neutral', labelKey: `${VERDICT_KEY}.notEnoughData` };
}
